<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useUserPreferencesStore } from "@/store/storeUserPreferences.js"

import BaseInput from "@/components/BaseComponents/BaseInput.vue"
import BaseProgressBar from "@/components/BaseComponents/BaseProgressBar.vue"

const userPreferencesStore = useUserPreferencesStore()
const { userPreferences } = storeToRefs(userPreferencesStore)

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true }
})

const emit = defineEmits(['update:modelValue'])
const hasError = ref(false)
const errorMessage = ref('')
const saving = ref(false)
const tableStore = useTableStore()

const formattedValue = computed(() => {
  if (props.modelValue instanceof Date) {
    // Håndtering av Date-objekter
    return formatTime(props.modelValue);
  }

  if (typeof props.modelValue === 'string' && props.modelValue) {
    // Håndtering av datetime-strenger fra backend
    const parsedDate = parseTime(props.modelValue);
    if (parsedDate) {
      return formatTime(parsedDate);
    }
  }

  return props.modelValue; // Fall-back
});

function formatTime (date: Date | null): string {
  if (!date) return '';

  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds();
  const milliseconds = date.getUTCMilliseconds();

  // Start med "HH:MM"
  let time = `${hours}:${minutes}`;

  // Legg til sekunder hvis de ikke er 0
  if (seconds > 0) {
    time += `:${seconds.toString().padStart(2, '0')}`;
  }

  // Legg til millisekunder hvis de finnes
  if (milliseconds > 0) {
    time += `.${milliseconds.toString().padStart(3, '0')}`;
  }

  return time;
}

function parseTime (input: string): Date | null {
  if (!input) return null

  let [datepart, timepart] = input.split('T');
  if (!timepart) {
    timepart = datepart
  }
  // Allow compact formats like "1055" => 10:55, "955" => 09:55
  const compact = timepart.replaceAll(' ', '')
  if (!timepart.includes(':')) {
  const m = compact.match(/^(\d{3,6})([.,]\d{1,3})?$/)
    if (m) {
      const core = m[1]
      const frac = m[2]?.slice(1) || ''
      let hrs = '00'
      let mins = '00'
      let secs = '00'
      if (core.length === 3 || core.length === 4) {
        mins = core.slice(-2)
        hrs = core.slice(0, core.length - 2)
      } else if (core.length === 5 || core.length === 6) {
        secs = core.slice(-2)
        mins = core.slice(-4, -2)
        hrs = core.slice(0, core.length - 4)
      }
      if (hrs.length === 1) hrs = `0${hrs}`
      if (mins.length === 1) mins = `0${mins}`
      if (secs.length === 1) secs = `0${secs}`
      const ms = frac ? frac.padEnd(3, '0').slice(0, 3) : ''
      timepart = secs === '00' && !frac ? `${hrs}:${mins}` : `${hrs}:${mins}:${secs}${ms ? `.${ms}` : ''}`
    }
  }
  const [hours, minutes, secondsPart] = timepart.split(':')
  let seconds = 0
  let milliseconds = 0

  if (secondsPart) {
    const [secs, ms] = secondsPart.split('.')
    seconds = parseInt(secs)
    milliseconds = ms ? parseInt(ms) : 0
  }

  const hour = parseInt(hours as string)
  const minute = parseInt(minutes as string)
  if (hour > 23 || hour < 0 ||
    minute > 59 || minute < 0 ||
    seconds > 59 || seconds < 0) {
    console.warn('parse error: ', hour, minute, secondsPart);
    return null
  }

  return new Date(Date.UTC(1900, 1, 1,
    hour, minute, seconds, milliseconds))
}

async function changed (newValue: string | null) {
  if (newValue === '' || newValue === null) {
    if (props.header.isNullable) {
      emit('update:modelValue', null)
      if (!props.autoSave) return;
      const item = { rowId: props.rowId, databaseObjectId: props.header.databaseObjectId, columnName: props.header.field, oldValue: props.modelValue, newValue: null };
      hasError.value = false
      saving.value = true
      await tableStore.saveData(item);
      saving.value = false;
      return;
    } else {
      hasError.value = true;
      errorMessage.value = 'Feltet kan ikke være tomt';
      return;
    }
  }
  const modelValueAsDate = parseTime(newValue)

  if (!modelValueAsDate) {
    hasError.value = true
    errorMessage.value = 'Ugyldig tidsformat. Bruk HH:MM:SS.fff'
    return
  }

  emit('update:modelValue', modelValueAsDate)

  if (!props.autoSave) return

  const item = {
    rowId: props.rowId,
    databaseObjectId: props.header.databaseObjectId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: modelValueAsDate
  }

  hasError.value = false
  saving.value = true
  await tableStore.saveData(item)
  saving.value = false
}

</script>

<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'right-align': singleLine }" class="verdi">
        <template v-if="modelValue !== null && modelValue !== undefined && userPreferences">
          {{ formattedValue }}
        </template>
        <template v-else>
          <span class="verdi muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="input-number-container">
        <div class="input-wrapper">
          <BaseInput
            :clearable="header.isNullable"
            :error="hasError"
            :error-message="errorMessage"
            :model-value="formattedValue"
            type="text"
            @change="changed"
          />
        </div>
      </div>
      <BaseProgressBar v-if="saving"/>
      <div v-else style="height: 4px;padding: 0;margin: 0;"/>
    </template>
  </div>
</template>

<style scoped>
.right-align {
  text-align: right;
  width: 100%;
}

.verdi {
  padding: 8px;
}

.verdi.muted {
  color: var(--gray-500);
  font-style: italic;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-500);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button:hover {
  background-color: var(--gray-200);
  color: var(--gray-700);
}
</style>
